// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
} from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState, xCreateAccountActions } from "@rpsc/App/Store/Reducers";
import CustomerAddressFormFields from "@rpsc/Overrides/Components/CustomerAddressFormFields";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    formId: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    billTo: state.xCreateAccount.accountInfo.billingAndShippingInfo.billTo,
});

const mapDispatchToProps = {
    setAddress: (address: Partial<BillToModel>) => (dispatch: Dispatch<xCreateAccountActions>) => {
        dispatch({
            type: "xCreateAccount/SetBillingAddress",
            address,
        });
    },
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CreateAccountBillingAddressFormStyles {
    wrapper?: InjectableCss;
}

export const createAccountBillingAddressFormStyles: CreateAccountBillingAddressFormStyles = {};

const styles = createAccountBillingAddressFormStyles;

const CreateAccountBillingAddressForm = ({ countries, fieldDisplay, formId, billTo, setAddress }: Props) => {
    const {
        firstName = "",
        lastName = "",
        attention = "",
        companyName = "",
        address1 = "",
        address2 = "",
        address3 = "",
        address4 = "",
        country = null,
        city = "",
        state = null,
        postalCode = "",
        phone = "",
        email = "",
        fax = "",
        validation,
    } = billTo || {};

    if (!validation) {
        return null;
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddress({
            [event.target.name]: event.target.value,
        });
    };

    const handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        setAddress({
            country,
        });
    };

    const handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        setAddress({
            state,
        });
    };

    const handleAddressChange = (newAddress: Partial<BillToModel>) => {
        setAddress({
            ...newAddress,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                onChangeFirstName={handleInputChange}
                onChangeLastName={handleInputChange}
                onChangeAttention={handleInputChange}
                onChangeCompanyName={handleInputChange}
                onChangeAddress1={handleInputChange}
                onChangeAddress2={handleInputChange}
                onChangeAddress3={handleInputChange}
                onChangeAddress4={handleInputChange}
                onChangeCountry={handleCountryChange}
                onChangeState={handleStateChange}
                onChangeCity={handleInputChange}
                onChangePostalCode={handleInputChange}
                onChangePhone={handleInputChange}
                onChangeEmail={handleInputChange}
                onChangeFax={handleInputChange}
                firstNameError=""
                lastNameError=""
                attentionError=""
                companyNameError=""
                address1Error=""
                address2Error=""
                address3Error=""
                address4Error=""
                countryError=""
                stateError=""
                cityError=""
                postalCodeError=""
                phoneError=""
                emailError=""
                faxError=""
                countries={countries}
                validation={validation}
                fieldDisplay={fieldDisplay}
                hideAdditionalAddresses={false}
                {...{ formId, addressUpdate: handleAddressChange }}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccountBillingAddressForm);
